<template>
  <section class="relative py-16 sm:py-24 overflow-hidden">
    <MotifBackdrop
      :show-grid="false"
      :show-tiled-pattern="false"
      :show-paper-field="false"
      :random-icons="true"
      :random-density="1"
      :size-min="28"
      :size-max="60"
      :opacity-min="0.10"
      :opacity-max="0.28"
      :kinds="['logo', 'code', 'terminal', 'braces', 'db', 'link']"
      :weights="{ logo: 2, code: 1.5, terminal: 1, mail: 0 }"
      :seed="2025"
    />

    <div class="relative z-10 max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
      
      <div class="flex flex-col items-center text-center mb-16 sm:mb-20">
        
        <div class="mb-6" v-if="isEditMode">
          <button
            @click="editServices"
            class="flex items-center gap-2 px-4 py-2 text-xs font-bold uppercase tracking-wider text-ink-600 bg-white border border-ink-200 rounded-full shadow-sm hover:text-brand-600 hover:border-brand-300 transition-all active:scale-95"
          >
            <i class="fas fa-pen-to-square"></i>
            <span>{{ effectiveLang === 'en' ? 'Edit Services' : 'Éditer les services' }}</span>
          </button>
        </div>

        <h2 class="text-4xl sm:text-5xl font-extrabold text-slate-900 tracking-tight mb-6">
          {{ serviceTitle?.titre || (effectiveLang === 'en' ? 'What We Do Best' : 'Nos Expertises') }}
        </h2>
        
        <div class="w-24 h-1.5 rounded-full bg-brand-600 mb-6 opacity-20"></div>

        <p v-if="serviceTitle?.description" class="max-w-2xl text-lg sm:text-xl text-slate-600 leading-relaxed">
          {{ serviceTitle.description }}
        </p>
      </div>

      <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 sm:gap-10">
        
        <template v-if="loading">
          <div v-for="n in 6" :key="'skel-' + n" class="bg-white rounded-[2rem] p-4 border border-slate-100 shadow-sm">
            <div class="aspect-[4/3] w-full bg-slate-100 rounded-3xl animate-pulse mb-6"></div>
            <div class="space-y-3 px-2 pb-4">
              <div class="h-8 w-3/4 bg-slate-100 rounded-lg animate-pulse"></div>
              <div class="h-4 w-full bg-slate-100 rounded animate-pulse"></div>
              <div class="h-4 w-5/6 bg-slate-100 rounded animate-pulse"></div>
            </div>
          </div>
        </template>

        <div
          v-else-if="displayServices.length === 0"
          class="col-span-full flex flex-col items-center justify-center py-20 px-6 rounded-[2.5rem] border-2 border-dashed border-slate-200 bg-slate-50/50"
        >
          <div class="h-16 w-16 bg-white rounded-full shadow-sm grid place-items-center text-slate-400 mb-4 text-2xl">
            <i class="fa-regular fa-folder-open"></i>
          </div>
          <h3 class="text-lg font-bold text-slate-900">
            {{ effectiveLang === 'en' ? 'No services found' : 'Aucun service disponible' }}
          </h3>
          <p class="text-slate-500 mt-1">
             {{ popularOnly ? "Try removing the popular filter." : "Add services via the admin panel." }}
          </p>
        </div>

        <article
          v-else
          v-for="(srv, index) in displayServices"
          :key="srv?.id || index"
          class="group flex flex-col bg-white rounded-[2rem] overflow-hidden border border-slate-100 shadow-[0_10px_40px_-15px_rgba(0,0,0,0.05)] hover:shadow-[0_25px_50px_-12px_rgba(0,0,0,0.1)] hover:border-brand-100 transition-all duration-300 hover:-translate-y-2"
        >
          <div class="relative aspect-[16/10] overflow-hidden m-2 rounded-[1.5rem]">
            <img
              :src="srv.image_url || placeholder"
              :alt="imageAlt(srv)"
              class="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-110"
              loading="lazy"
            />
            
            <div class="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent opacity-60"></div>

            <div class="absolute bottom-4 left-4 h-12 w-12 flex items-center justify-center rounded-xl bg-white/20 backdrop-blur-md border border-white/30 text-white shadow-lg">
               <i :class="iconClassFromService(srv)" class="text-lg drop-shadow-md"></i>
            </div>

            <div
              v-if="Number(srv.is_popular) === 1"
              class="absolute top-4 right-4 flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-amber-400/90 backdrop-blur-sm text-amber-950 text-xs font-bold shadow-sm"
            >
              <i class="fa-solid fa-star text-[10px]"></i>
              <span>Top</span>
            </div>

            <div
              v-if="srv.badge"
              class="absolute top-4 left-4 px-3 py-1.5 rounded-full bg-slate-900/80 backdrop-blur-sm text-white text-xs font-bold shadow-sm border border-white/10"
            >
              {{ srv.badge }}
            </div>
          </div>

          <div class="flex-1 p-6 flex flex-col">
            <h3 class="text-xl sm:text-2xl font-bold text-slate-900 group-hover:text-brand-600 transition-colors">
              {{ srv.titre || (effectiveLang === 'en' ? 'Service' : 'Service') }}
            </h3>

            <p class="mt-3 text-slate-600 leading-relaxed line-clamp-3 mb-6">
              {{ srv.description || (effectiveLang === 'en' ? 'Description...' : 'Description du service...') }}
            </p>

            <div class="mt-auto pt-4 border-t border-slate-100">
               <router-link
                :to="makeServiceLink(srv)"
                class="inline-flex items-center gap-2 text-sm font-bold text-slate-900 hover:text-brand-600 transition-colors group/link"
              >
                <span>{{ effectiveLang === 'en' ? 'Learn more' : 'En savoir plus' }}</span>
                <span class="bg-slate-100 rounded-full w-6 h-6 flex items-center justify-center group-hover/link:bg-brand-100 group-hover/link:text-brand-600 transition-colors">
                  <i class="fa-solid fa-arrow-right text-[10px] transform group-hover/link:translate-x-0.5 transition-transform"></i>
                </span>
              </router-link>
            </div>
          </div>
        </article>
      </div>

      <div class="mt-16 sm:mt-24 text-center">
        <div class="inline-flex flex-col sm:flex-row items-center gap-4 bg-white p-2 rounded-full border border-slate-100 shadow-[0_10px_40px_-10px_rgba(0,0,0,0.08)]">
           <router-link
            v-for="(button, idx) in serviceButtons"
            :key="'cta-' + idx"
            :to="button.action || '#'"
            class="px-8 py-3 rounded-full text-sm font-bold transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-offset-2"
            :class="[
              (button.style === 'primary')
                ? 'bg-brand-600 text-white shadow-md hover:bg-brand-700 hover:shadow-lg'
                : 'text-slate-600 hover:text-brand-600 hover:bg-slate-50'
            ]"
          >
            {{ button.label || (effectiveLang === 'en' ? 'Discover' : 'Découvrir') }}
            <i v-if="button.style === 'primary'" class="fas fa-arrow-right ml-2 text-xs"></i>
          </router-link>
        </div>
      </div>

    </div>
  </section>
</template>

<script>
// On garde exactement le même script qu'avant
import http from "@/api/http";
import { fetchPageIdByCode } from "@/utils/pageUtils";
import { mapGetters } from "vuex";
import MotifBackdrop from "@/components/ui/MotifBackdrop.vue";

export default {
  name: "ServicesComponent",
  components: { MotifBackdrop },
  props: {
    isEditMode: { type: Boolean, default: false },
    pageId: { type: [Number, String], default: null },
    pageCode: { type: String, default: "home" },
    langue: { type: String, default: null },
    popularOnly: { type: Boolean, default: false },
    limit: { type: [Number, String], default: null },
  },
  data() {
    return {
      loading: true,
      serviceTitle: null,
      services: [],
      serviceButtons: [],
      _resolvedPageId: null,
      placeholder: "https://placehold.co/1200x675?text=Service",
    };
  },
  computed: {
    ...mapGetters(["getLangue"]),
    effectiveLang() {
      return this.langue || this.getLangue || "fr";
    },
    effectivePageCode() {
      if (this.pageCode) return this.pageCode;
      const n = this.$route?.name || "";
      if (/about/i.test(n)) return "about";
      if (/portfolio/i.test(n)) return "portfolio";
      if (/contact/i.test(n)) return "contact";
      if (/footer/i.test(n)) return "footer";
      return "home";
    },
    primaryCta() {
      const first = (this.serviceButtons || []).find(
        (b) => (b.style || "").toLowerCase() === "primary"
      );
      return first?.action || (this.effectiveLang === "en" ? "/en/contact" : "/fr/contact");
    },
    displayServices() {
      let arr = Array.isArray(this.services) ? [...this.services] : [];

      if (this.popularOnly) {
        arr = arr.filter(s => Number(s?.is_popular) === 1);
        arr.sort((a, b) => {
          const ra = Number(a?.popular_rank ?? 9999);
          const rb = Number(b?.popular_rank ?? 9999);
          return ra - rb || (Number(b?.id || 0) - Number(a?.id || 0));
        });
      }

      if (this.limit && Number(this.limit) > 0) {
        arr = arr.slice(0, Number(this.limit));
      }
      return arr;
    },
  },
  watch: {
    pageId(newVal) {
      if (newVal) {
        this._resolvedPageId = Number(newVal);
        this.fetchByPageId(this._resolvedPageId);
      }
    },
    effectiveLang() {
      if (!this.pageId) this.autoResolveAndFetch();
    },
    "$route.fullPath"() {
      if (!this.pageId) this.autoResolveAndFetch();
    },
    effectivePageCode() {
      if (!this.pageId) this.autoResolveAndFetch();
    },
    isEditMode() {
      if (Number.isFinite(this._resolvedPageId)) {
        this.fetchByPageId(this._resolvedPageId);
      }
    },
    popularOnly() {
      if (Number.isFinite(this._resolvedPageId)) this.fetchByPageId(this._resolvedPageId);
    },
    limit() {
      if (Number.isFinite(this._resolvedPageId)) this.fetchByPageId(this._resolvedPageId);
    },
  },
  methods: {
    isIconToken(v) {
      return typeof v === "string" && v.trim().toLowerCase().startsWith("icon:");
    },
    iconClassFromToken(token) {
      const fallback = "fa-solid fa-briefcase";
      if (!this.isIconToken(token)) return fallback;
      const raw = token.slice(5).trim();
      const parts = raw.split(/\s+/).filter(Boolean);
      if (parts.length >= 2 && parts[0].startsWith("fa-") && parts[1].startsWith("fa-")) {
        return `${parts[0]} ${parts[1]}`;
      }
      if (parts.length === 1 && parts[0].startsWith("fa-")) {
        return `fa-solid ${parts[0]}`;
      }
      return fallback;
    },
    iconClassFromService(srv = {}) {
      const tok = srv.icon_alt || (this.isIconToken(srv.alt) ? srv.alt : null);
      return this.iconClassFromToken(tok);
    },
    iconTitle(srv = {}) {
      return srv.titre || "Service";
    },
    imageAlt(srv = {}) {
      if (this.isIconToken(srv.alt)) return srv.titre || "Service";
      return srv.alt || srv.titre || "Service";
    },
    async autoResolveAndFetch() {
      try {
        if (this.pageId) {
          this._resolvedPageId = Number(this.pageId);
        } else {
          const lang = this.effectiveLang;
          const code = this.effectivePageCode;
          const idOrObj = await fetchPageIdByCode(code, lang);
          const pid = typeof idOrObj === "number" ? idOrObj : idOrObj?.page_id ?? idOrObj?.id ?? null;
          this._resolvedPageId = Number(pid);
        }
        if (Number.isFinite(this._resolvedPageId)) {
          await this.fetchByPageId(this._resolvedPageId);
        } else {
           this.loading = false;
        }
      } catch (e) {
        console.error("[ServicesComponent] autoResolveAndFetch error:", e);
        this.loading = false;
      }
    },
    async fetchByPageId(pid) {
      this.loading = true;
      try {
        const params = {
          page_id: pid,
          ...(this.isEditMode ? { includeDraft: 1 } : {}),
          ...(this.popularOnly ? { popularOnly: 1 } : {}),
          ...(this.limit ? { limit: Number(this.limit) } : {}),
        };
        const { data } = await http.get("services", { params });

        this.serviceTitle = data?.serviceTitle || {};

        const raw = Array.isArray(data?.services) ? data.services : [];
        this.services = raw.map((s) => ({
          ...s,
          badge:
            this.isEditMode && s.status === "draft"
              ? (this.effectiveLang === "en" ? "Draft" : "Brouillon")
              : s.badge,
        }));

        this.serviceButtons = Array.isArray(data?.boutons) ? data.boutons : [];
      } catch (error) {
        console.error("Erreur Services (page_id):", error);
        this.serviceTitle = {};
        this.services = [];
        this.serviceButtons = [];
      } finally {
        this.loading = false;
      }
    },
    isExternal(href) {
      return /^https?:\/\//i.test(String(href || ""));
    },
    ensureLocalePrefix(path) {
      if (!path) return this.primaryCta || "#";
      if (this.isExternal(path)) return path;
      const lang = this.effectiveLang === "en" ? "en" : "fr";
      if (/^\/(fr|en)\b/.test(path)) return path.replace(/^\/(fr|en)/, `/${lang}`);
      return path.startsWith("/") ? `/${lang}${path}` : `/${lang}/${path}`;
    },
    makeServiceLink(srv) {
      if (srv?.slug) {
        return { name: 'service-detail', params: { langue: this.effectiveLang, slug: srv.slug } };
      }
      if (srv?.action) return this.ensureLocalePrefix(srv.action);
      return this.primaryCta || '#';
    },
    editServices() {
      this.$emit("edit", { serviceTitle: this.serviceTitle, services: this.services });
    },
    addServices() {
      this.$emit("add");
    },
  },
  async mounted() {
    if (this.pageId) {
      this._resolvedPageId = Number(this.pageId);
      await this.fetchByPageId(this._resolvedPageId);
    } else {
      await this.autoResolveAndFetch();
    }
  },
};
</script>

<style scoped>
/* On garde ça simple, le gros du travail est fait par Tailwind */
@media (prefers-reduced-motion: reduce) {
  .transition-all,
  .transition-transform,
  .animate-pulse {
    transition: none !important;
    animation: none !important;
  }
}
</style>